import React, { useState, useEffect, useRef } from 'react';
import {
  Add,
  ExpandMore,
  Remove
} from "@mui/icons-material";
import { Accordion, AccordionSummary, AccordionDetails, Tooltip, Fab } from "@mui/material";

import Grid from '@mui/material/Grid2';

import { 
  Character,
  CharacterAbility,
  CharacterSpell,
  CharacterAction,
  CharacterSheetInterface,
  SpellModification,
  SpellsFromList,
  StoredSpell,
} from "@dndModels";
import { DAMAGE_TYPES } from "@dndModels/Constants";
import { SpellLevel } from "@dndModels/Types";

import API from "@dndUtilities/system_api";
import DandDCharacterUtilities from "@dndUtilities/char_utilities";
import CommonUtilities from "@utilities/common_utilities";

import LoadingBox from "@components/input/LoadingBox";
import ButtonBox from "@components/input/buttons/ButtonBox";
import StringBox from "@components/input/strings/StringBox";
import CheckBox from "@components/input/CheckBox";

import ViewSpell from "@dndComponents/display/ViewSpell";
import SpellDetails from "@dndComponents/display/SpellDetails";
import ItemDetails from "@dndComponents/equipment/ItemDetails";

import CharacterEffects from "@dndComponents/character/spells_and_abilities/effects/CharacterEffects";

import CharacterCastButton from "@dndComponents/character/spells_and_abilities/CharacterCastButton";
import CharacterResourceBoxes from "@dndComponents/character/misc/CharacterResourceBoxes";
import SelectStringBox from "@components/input/selects/SelectStringBox";
import CharacterKnowledgeForUsage from "@dndComponents/character/knowledge/CharacterKnowledgeForUsage";
import DisplayStringWithReplacements from "@dndComponents/display/DisplayStringWithReplacements";
import ToggleButtonBox from "@components/input/buttons/ToggleButtonBox";
import StarBox from "@components/input/buttons/StarBox";

interface Props {
  character: Character;
  char_interface: CharacterSheetInterface;
  obj: CharacterSpell | CharacterAbility | StoredSpell;
  action: CharacterAction;
  level: SpellLevel;
  type: string; // Spell or Spell As Ability or ""
  shared: boolean;
  show_item?: boolean;
  onObjChange: (changed: (CharacterSpell | CharacterAbility)) => void;
  onClose: () => void;
}

const api = API.getInstance();
const char_util = DandDCharacterUtilities.getInstance();
const common_util = CommonUtilities.getInstance();

export default function CharacterSpellDetails({
  character,
  char_interface,
  obj,
  action,
  level,
  type,
  shared,
  show_item = false,
  onObjChange,
  onClose,
}: Props) {
  const [fetched, setFetched] = useState(false);
  const [selected_level, setSelectedLevel] = useState<SpellLevel>(-1);
  const [reloading, setReloading] = useState(false);
  const [ping, setPing] = useState(false);
  const [show_desc, setShowDesc] = useState(true);
  const [show_spell, setShowSpell] = useState(true);
  const mounted = useRef(false);

  useEffect(() => { 
    mounted.current = true;

    return () => {
      mounted.current = false;
    };
  }, []);

  const watchedChange = () => {
    setReloading(true);
  }

  const recalculated = () => {
    // console.log("recalculated");
    onClose();
  }

   
  useEffect(() => {
    if (!fetched) {
      load();
      character.subscription_holder.subscribe("Resources", mounted, watchedChange);
      character.subscription_holder.subscribe("Items", mounted, watchedChange);
      character.subscription_holder.subscribe("Spells", mounted, watchedChange);
      character.subscription_holder.subscribe("Recalculated", mounted, recalculated);
    }
  });
  
  useEffect(() => {
    if (reloading) {
      setReloading(false);
    }
  }, [reloading, setReloading]);
  
  const enabled = shared || (api.user && (character.owner_id === api.user._id || (character.campaign !== null && character.campaign.owner_id === api.user._id)));
    
  const load = () => {
    setSelectedLevel(level === -1 ? obj.level : level);
    setFetched(true);
  }

  const renderLevelOptions = () => {
    if (action.action instanceof StoredSpell) {

    } else if (action.infusion) {
      return (
        <CharacterResourceBoxes 
          used={ action.infusion.used ? 1 : 0 }
          total={1}
          true_total={1}
          created={0}
          type="Infusion"
          // label="Infusion"
          show_type={true}
          character={character}
          shared={shared}
          onChange={async (used: number) => {
            if (action.infusion) {
              action.infusion.used = used > 0;
              character.changed();
              await char_util.resourcesChanged(character);
              setPing(!ping);
            }
          }}
        />
      );
    } else if (selected_level > 0) { // and not at will and not ritual only
      const slot_levels = Array.from(new Set(character.use_slots.filter(o => o.level >= obj.level).map(o => o.level)));
      const slots = character.use_slots.filter(o => o.level === selected_level);
      if (slot_levels.length > 1) {
        return (
          <Grid container spacing={0} direction="row">
            <Grid
              size={{
                xs: 4
              }}>
              Level
            </Grid>
            <Grid
              container
              spacing={0}
              direction="row"
              size={{
                xs: 8
              }}>
              <Grid
                size={{
                  xs: 4
                }}>
                <ButtonBox
                  name=" - "
                  disabled={ selected_level === Math.min(...slot_levels) }
                  onClick={() => {
                    setSelectedLevel(Math.max(...slot_levels.filter(o => o < selected_level)) as SpellLevel);
                  }} 
                />
              </Grid>
              <Grid
                className="centered"
                size={{
                  xs: 4
                }}>
                { selected_level }
              </Grid>
              <Grid
                size={{
                  xs: 4
                }}>
                <ButtonBox
                  name=" + "
                  disabled={ selected_level === Math.max(...slot_levels) }
                  onClick={() => {
                    setSelectedLevel(Math.min(...slot_levels.filter(o => o > selected_level)) as SpellLevel);
                  }} 
                />
              </Grid>
            </Grid>
            { slots.map((slot, key) => {
              return (
                <Grid
                  key={key}
                  size={{
                    xs: 12
                  }}>
                  <CharacterResourceBoxes 
                    used={slot.used}
                    total={slot.total}
                    true_total={slot.true_total}
                    created={slot.created}
                    type={slot.slot_name}
                    show_type={false}
                    character={character}
                    shared={shared}
                    onChange={async (used: number) => {
                      slot.used = used;
                      character.changed();
                      await char_util.resourcesChanged(character);
                      setPing(!ping);
                    }}
                  />
                </Grid>
              );
            })}
          </Grid>
        );
      } else {
        return (
          <Grid container spacing={0} direction="row">
            { slots.map((slot, key) => {
              return (
                <Grid
                  key={key}
                  size={{
                    xs: 12
                  }}>
                  <CharacterResourceBoxes 
                    used={slot.used}
                    total={slot.total}
                    true_total={slot.true_total}
                    created={slot.created}
                    type={slot.slot_name}
                    show_type={false}
                    character={character}
                    shared={shared}
                    onChange={async (used: number) => {
                      slot.used = used;
                      character.changed();
                      await char_util.resourcesChanged(character);
                      setPing(!ping);
                    }}
                  />
                </Grid>
              );
            })}
          </Grid>
        );
      }
    }
    return null;
  }

  const renderModifications = (modifications: SpellModification[]) => {
    if (obj instanceof CharacterSpell && obj.spell) {
      const spell = obj.spell;
      return (
        <Grid
          container
          spacing={0}
          direction="row"
          size={{
            xs: 12
          }}>
          { modifications.map((mod, key) => {
            let mod_type_options = ["Components","Concentration","Damage Type","Range","Ritual","Targets","None"];

            if (!obj.concentration || (mod.mod_type !== "Concentration" && obj.concentration_modifications.length > 0)) {
              mod_type_options = mod_type_options.filter(o => o !== "Concentration");
            }

            const component_modifications = obj.component_modifications.filter(o => o.true_id !== mod.true_id);
            const removed_components = component_modifications.map(o => o.detail);
            let component_options = spell.components.filter(o => !removed_components.includes(o));
            if (component_options.includes("M") && obj.material_consumed) {
              component_options = component_options.filter(o => o !== "M");
            }
            if (component_options.length === 0) {
              mod_type_options = mod_type_options.filter(o => o !== "Components");
            }
            
            if (!common_util.anyOverlaps(spell.effect_types, DAMAGE_TYPES) || (mod.mod_type !== "Damage Type" && obj.damage_type_modifications.length > 0)) {
              mod_type_options = mod_type_options.filter(o => o !== "Damage Type");
            }
            
            if (spell.ritual || ["A","BA","RA","Attack","1 Minute"].includes(spell.casting_time) || (mod.mod_type !== "Ritual" && obj.ritual_modifications.length > 0)) {
              mod_type_options = mod_type_options.filter(o => o !== "Ritual");
            }
            
            if (mod.mod_type !== "Targets" && obj.targets_modifications.length > 0) {
              mod_type_options = mod_type_options.filter(o => o !== "Targets");
            }

            let detail_options: string[] = [];
            let detail_name = "";
            switch (mod.mod_type) {
              case "Components": 
                detail_name = "Removing Component";
                detail_options = component_options;
              break;
              case "Concentration":break;
              case "Damage Type": 
                detail_name = "New Damage Type";
                detail_options = ["Acid","Cold","Fire","Lightning","Necrotic","Poison","Thunder"];
              break;
              case "None":break;
              case "Range": break;
              case "Ritual": break;
              case "Targets": 
                detail_name = "Valid Targets";
                detail_options = ["Allies Only","Enemies Only"];
              break;
            }
            return (
              <Grid
                key={key}
                container
                spacing={0}
                direction="row"
                size={{
                  xs: 12
                }}>
                <Grid
                  size={{
                    xs: 4
                  }}>
                  <SelectStringBox
                    name="Type"
                    options={mod_type_options}
                    value={mod.mod_type}
                    onChange={(changed: string) => {
                      (mod.mod_type as string) = changed;
                      setPing(!ping);
                      onObjChange(obj);
                    }}
                  />
                </Grid>
                <Grid
                  size={{
                    xs: 4
                  }}>
                  { detail_options.length > 0 &&
                    <SelectStringBox
                      name={detail_name}
                      options={detail_options}
                      value={mod.detail}
                      onChange={(changed: string) => {
                        mod.detail = changed;
                        setPing(!ping);
                        onObjChange(obj);
                      }}
                    />
                  }
                </Grid>
                <Grid
                  size={{
                    xs: 4
                  }}>
                  <Tooltip title={`Remove Modification`}>
                    <Fab size="small" color="primary" style={{marginLeft: "8px"}}
                      onClick={ () => {
                        if (obj instanceof CharacterSpell) {
                          const new_obj = obj;
                          new_obj.modifications = new_obj.modifications.filter(o => o.true_id !== mod.true_id);
                          new_obj.custom_modifications = new_obj.custom_modifications.filter(o => o.true_id !== mod.true_id);
                          setPing(!ping);
                          character.subscription_holder.displayChanged(); // onObjChange(new_obj);
                        }
                      }}>
                      <Remove/>
                    </Fab>
                  </Tooltip>
                </Grid>
              </Grid>
            );
          })}
        </Grid>
      );
    }
    return null;
  }

  const customizationChanged = () => {
    character.changed();
    character.subscription_holder.displayChanged();
    setPing(!ping);
  }

  const renderCustomizations = () => {
    // debugger;
    if (!(obj instanceof StoredSpell)) {
      let main_customizations: any = null;
      if (obj instanceof CharacterSpell) {
        const upcast_options: string[] = [];
        const slot_levels = character.use_slots.map(o => o.level);
        for (let i = obj.level + 1; i <= Math.max(...slot_levels); ++i) {
          upcast_options.push(`${i}`);
        }
        main_customizations = (
          <Grid
            style={{ fontWeight: "bold" }}
            size={{
              xs: 12
            }}>
            <Accordion>
              <AccordionSummary 
                style={{
                  fontWeight: "bold",
                  height: "30px"
                }}
                expandIcon={<ExpandMore />}>
                Customize
              </AccordionSummary>
              <AccordionDetails>
                <div>
                  <Grid container spacing={0} direction="row">
                    <Grid
                      size={{
                        xs: 12
                      }}>
                      <StringBox
                        name="Name"
                        disabled={ !enabled }
                        value={ obj._name }
                        onBlur={(value: string) => {
                          if (obj instanceof CharacterSpell) {
                            const new_obj = obj;
                            new_obj._name = value;
                            customizationChanged();
                          }
                        }}
                      />
                    </Grid>
                    <Grid
                      size={{
                        xs: 12
                      }}>
                      <CheckBox
                        name={ "Hide on Actions" }
                        disabled={ !enabled }
                        value={ obj.hidden }
                        onChange={(value: boolean) => {
                          if (obj instanceof CharacterSpell) {
                            const new_obj = obj;
                            new_obj.hidden = value;
                            customizationChanged();
                          }
                        }}
                      />
                    </Grid>
                    { upcast_options.length > 0 &&
                      <Grid
                        size={{
                          xs: 12
                        }}>
                        <SelectStringBox
                          name="Show Upcasts"
                          disabled={ !enabled }
                          values={ obj.show_upcasts.map(o => `${o}`) }
                          multiple
                          options={upcast_options}
                          onChange={(values: string[]) => {
                            const new_obj = obj;
                            new_obj.show_upcasts = values.map(o => +o);
                            customizationChanged();
                          }}
                        />
                      </Grid>
                    }
                    { obj.ritual &&
                      <Grid
                        size={{
                          xs: 12
                        }}>
                        <CheckBox
                          name={ "Hide Ritual on Actions" }
                          disabled={ !enabled }
                          value={ obj.hide_ritual }
                          onChange={(value: boolean) => {
                            if (obj instanceof CharacterSpell) {
                              const new_obj = obj;
                              new_obj.hide_ritual = value;
                              customizationChanged();
                            }
                          }}
                        />
                      </Grid>
                    }
                    <Grid
                      size={{
                        xs: 12
                      }}>
                      <CheckBox
                        name={ "Hide in PDF" }
                        disabled={ !enabled }
                        value={ obj.hide_in_pdf }
                        onChange={(value: boolean) => {
                          if (obj instanceof CharacterSpell) {
                            const new_obj = obj;
                            new_obj.hide_in_pdf = value;
                            customizationChanged();
                          }
                        }}
                      />
                    </Grid>
                    { upcast_options.length > 0 &&
                      <Grid
                        size={{
                          xs: 12
                        }}>
                        <SelectStringBox
                          name="Show Upcasts in PDF"
                          disabled={ !enabled }
                          values={ obj.show_upcasts_in_pdf.map(o => `${o}`) }
                          multiple
                          options={upcast_options}
                          onChange={(values: string[]) => {
                            if (obj instanceof CharacterSpell) {
                              const new_obj = obj;
                              new_obj.show_upcasts_in_pdf = values.map(o => +o);
                              customizationChanged();
                            }
                          }}
                        />
                      </Grid>
                    }
                    { obj.ritual &&
                      <Grid
                        size={{
                          xs: 12
                        }}>
                        <CheckBox
                          name={ "Hide Ritual in PDF" }
                          disabled={ !enabled }
                          value={ obj.hide_ritual_in_pdf }
                          onChange={(value: boolean) => {
                            if (obj instanceof CharacterSpell) {
                              const new_obj = obj;
                              new_obj.hide_ritual_in_pdf = value;
                              customizationChanged();
                            }
                          }}
                        />
                      </Grid>
                    }
                    <Grid
                      size={{
                        xs: 3
                      }}>
                      { enabled &&
                        <Tooltip title={`Create New Modification`}>
                          <Fab size="small" color="primary" style={{marginLeft: "8px"}}
                            onClick={ () => {
                              if (obj instanceof CharacterSpell) {
                                const new_obj = obj;
                                new_obj.custom_modifications.push(new SpellModification());
                                customizationChanged();
                              }
                            }}>
                            <Add/>
                          </Fab>
                        </Tooltip>
                      }
                    </Grid>
                    <Grid
                      className="header"
                      size={{
                        xs: 9
                      }}>Modifications</Grid>
                    { renderModifications(obj.custom_modifications) }
                  </Grid>
                </div>
              </AccordionDetails>
            </Accordion>
          </Grid>
        );
      } else {
        if (obj.the_ability instanceof SpellsFromList) {
          const the_ability = obj.the_ability;
          const upcast_options: string[] = [];
          if (the_ability.spell_overrides.slot_override === "Don't Override" || 
            the_ability.spell_overrides.slot_override === "Or Special Resource" || 
            the_ability.spell_overrides.slot_override === "And Special Resource" || 
            the_ability.spell_overrides.slot_override === "At Will or Slot Upcast") {
            const slot_levels = character.use_slots.map(o => o.level);
            let lowest = obj.level;
            if (the_ability.spell_overrides.slot_override === "At Will or Slot Upcast") {
              lowest++;
            }
            lowest = Math.min(...slot_levels.filter(o => o >= lowest)) as SpellLevel;
            for (let i = lowest + 1; i <= Math.max(...slot_levels); ++i) {
              upcast_options.push(`${i}`);
            }
          }
          main_customizations = (
            <Grid style={{ fontWeight: "bold" }}>
              <Accordion>
                <AccordionSummary 
                  style={{
                    fontWeight: "bold",
                    height: "30px"
                  }}
                  expandIcon={<ExpandMore />}>
                  Customize
                </AccordionSummary>
                <AccordionDetails>
                  <div>
                    <Grid container spacing={0} direction="row">
                      <Grid
                        size={{
                          xs: 12
                        }}>
                        <StringBox
                          name="Name"
                          disabled={ !enabled }
                          value={ obj._name }
                          onBlur={(value: string) => {
                            const new_obj = obj;
                            new_obj._name = value;
                            customizationChanged();
                          }}
                        />
                      </Grid>
                      <Grid
                        size={{
                          xs: 12
                        }}>
                        <CheckBox
                          name={ "Hide on Actions" }
                          disabled={ !enabled }
                          value={ obj.hidden }
                          onChange={(value: boolean) => {
                            const new_obj = obj;
                            new_obj.hidden = value;
                            customizationChanged();
                          }}
                        />
                      </Grid>
                      { upcast_options.length > 0 &&
                        <Grid
                          size={{
                            xs: 12
                          }}>
                          <SelectStringBox
                            name="Show Upcasts"
                            disabled={ !enabled }
                            values={ obj.show_upcasts.map(o => `${o}`) }
                            multiple
                            options={upcast_options}
                            onChange={(values: string[]) => {
                              const new_obj = obj;
                              new_obj.show_upcasts = values.map(o => +o);
                              customizationChanged();
                            }}
                          />
                        </Grid>
                      }
                      { the_ability.spell_overrides.slot_override === "Or Special Resource" &&
                        <Grid
                          size={{
                            xs: 12
                          }}>
                          <CheckBox
                            name={ "Hide Special" }
                            disabled={ !enabled }
                            value={ obj.hide_special }
                            onChange={(value: boolean) => {
                              const new_obj = obj;
                              new_obj.hide_special = value;
                              customizationChanged();
                            }}
                          />
                        </Grid>
                      }
                      { obj.ritual &&
                        <Grid
                          size={{
                            xs: 12
                          }}>
                          <CheckBox
                            name={ "Hide Ritual on Actions" }
                            disabled={ !enabled }
                            value={ obj.hide_ritual }
                            onChange={(value: boolean) => {
                              if (obj instanceof CharacterSpell) {
                                const new_obj = obj;
                                new_obj.hide_ritual = value;
                                customizationChanged();
                              }
                            }}
                          />
                        </Grid>
                      }
                      <Grid
                        size={{
                          xs: 12
                        }}>
                        <CheckBox
                          name={ "Hide in PDF" }
                          disabled={ !enabled }
                          value={ obj.hide_in_pdf }
                          onChange={(value: boolean) => {
                            const new_obj = obj;
                            new_obj.hide_in_pdf = value;
                            customizationChanged();
                          }}
                        />
                      </Grid>
                      { upcast_options.length > 0 &&
                        <Grid
                          size={{
                            xs: 12
                          }}>
                          <SelectStringBox
                            name="Show Upcasts in PDF"
                            disabled={ !enabled }
                            values={ obj.show_upcasts_in_pdf.map(o => `${o}`) }
                            multiple
                            options={upcast_options}
                            onChange={(values: string[]) => {
                              if (obj instanceof CharacterSpell) {
                                const new_obj = obj;
                                new_obj.show_upcasts_in_pdf = values.map(o => +o);
                                customizationChanged();
                              }
                            }}
                          />
                        </Grid>
                      }
                      { the_ability.spell_overrides.slot_override === "Or Special Resource" &&
                        <Grid
                          size={{
                            xs: 12
                          }}>
                          <CheckBox
                            name={ "Hide Special in PDF" }
                            disabled={ !enabled }
                            value={ obj.hide_special_in_pdf }
                            onChange={(value: boolean) => {
                              const new_obj = obj;
                              new_obj.hide_special_in_pdf = value;
                              customizationChanged();
                            }}
                          />
                        </Grid>
                      }
                    </Grid>
                  </div>
                </AccordionDetails>
              </Accordion>
            </Grid>
          );
        // } else if (obj.the_ability instanceof SpellsFromListWithCosts) {
        //   main_customizations = (
        //     <Grid item style={{ fontWeight: "bold" }}>Customizations for SpellsFromListWithCosts Not Implemented</Grid>
        //   );
        } else {
          main_customizations = (
            <Grid style={{ fontWeight: "bold" }}>
              <Accordion>
                <AccordionSummary 
                  style={{
                    fontWeight: "bold",
                    height: "30px"
                  }}
                  expandIcon={<ExpandMore />}>
                  Customize
                </AccordionSummary>
                <AccordionDetails>
                  <div>
                    <Grid container spacing={0} direction="row">
                      <Grid
                        size={{
                          xs: 12
                        }}>
                        <StringBox
                          name="Name"
                          disabled={ !enabled }
                          value={ obj._name }
                          onBlur={(value: string) => {
                            const new_obj = obj;
                            new_obj._name = value;
                            customizationChanged();
                          }}
                        />
                      </Grid>
                      <Grid
                        size={{
                          xs: 12
                        }}>
                        <CheckBox
                          name={ "Hide" }
                          disabled={ !enabled }
                          value={ obj.hidden }
                          onChange={(value: boolean) => {
                            const new_obj = obj;
                            new_obj.hidden = value;
                            customizationChanged();
                          }}
                        />
                      </Grid>
                      <Grid
                        size={{
                          xs: 12
                        }}>
                        <CheckBox
                          name={ "Hide in PDF" }
                          disabled={ !enabled }
                          value={ obj.hide_in_pdf }
                          onChange={(value: boolean) => {
                            const new_obj = obj;
                            new_obj.hide_in_pdf = value;
                            customizationChanged();
                          }}
                        />
                      </Grid>
                    </Grid>
                  </div>
                </AccordionDetails>
              </Accordion>
            </Grid>
          );
        }
      }
      return (
        <>
          { obj.knowledge_usage_key !== "" &&
            <Grid
              style={{ fontWeight: "bold" }}
              size={{
                xs: 12
              }}>
              <Accordion>
                <AccordionSummary 
                  style={{
                    fontWeight: "bold",
                    height: "30px"
                  }}
                  expandIcon={<ExpandMore />}>
                  {obj.knowledge_usage_key} Knowledge
                </AccordionSummary>
                <AccordionDetails>
                  <div>
                    <CharacterKnowledgeForUsage 
                      character={character}
                      selected_usage={obj.knowledge_usage_key}
                    />
                  </div>
                </AccordionDetails>
              </Accordion>
            </Grid>
          }
          { main_customizations }
        </>
      );
    }
    return null;
  }

  // debugger;
  if (!fetched) {
    return (<LoadingBox />);
  } else if (obj instanceof CharacterSpell || obj instanceof StoredSpell) {
    const the_spell = obj.the_spell;
    let show_ritual = false;
    // let show_modifications = false;
    if (obj instanceof CharacterSpell && obj.source_type === "Class" && character instanceof Character) {
      // Check if their class can do rituals, and/or modify spells
      const char_class = character.classes.find(o => o.source_id === obj.source_id);
      if (char_class) {
        show_ritual = char_class.ritual_casting;
        // if (char_class.spellbook && char_class.spellbook.spellbook && char_class.spellbook.spellbook.can_modify_spells_at_level > -1) {
        //   show_modifications = char_class.level >= char_class.spellbook.spellbook.can_modify_spells_at_level;
        // }
      }
    } 
    if (the_spell) {
      return (
        <div 
          style={{ 
            padding: "4px",
            fontSize: "11px"
          }}>
          <Grid container spacing={0} direction="row"
            style={{ 
              overflowX: "hidden"
            }}>
            <Grid
              size={{
                xs: 6
              }}>
              <ViewSpell spell={obj}
                show_level 
                show_ritual={show_ritual}
                fontSize={20}
                fontWeight={"bold"}
                character={character}
              />
            </Grid>
            <Grid
              size={{
                xs: 6
              }}>
              <StarBox
                name="Favorite"
                value={obj.favorite}
                onChange={(changed: boolean) => {
                  obj.favorite = changed;
                  customizationChanged();
                }}
              />
            </Grid>
            <Grid
              size={{
                xs: 12
              }}>
              { obj.level_and_school }
            </Grid>
            <Grid
              size={{
                xs: 6
              }}>
              { !reloading &&
                <CharacterCastButton
                  obj={obj}
                  action={action}
                  character={character}
                  char_interface={char_interface}
                  level={selected_level}
                  disabled={!enabled && !shared}
                  shared={shared}
                  // onChange={async () => {
                  //   character.changed();
                  //   await char_util.resourcesChanged(character);
                  //   setPing(!ping);
                  // }}
                />
              }
            </Grid>
            <Grid
              size={{
                xs: 6
              }}>
              { renderLevelOptions() }
            </Grid>
            <Grid
              size={{
                xs: 12
              }}> 
              { !reloading &&
                <CharacterEffects 
                  char={character}
                  char_interface={char_interface}
                  obj={obj}
                  action={action}
                  level={selected_level}
                  wide
                  casting_time={action.casting_time}
                  disabled={!enabled}
                  shared={shared}
                  show_extra_effects
                  onChange={() => {
                    character.subscription_holder.recalculate();
                  }}
                  // onTransform={() => {
                  //   onTransform();
                  // }}
                />
              }
            </Grid> 
            { renderCustomizations() }
            <Grid
              size={{
                xs: 12
              }}>
              <ToggleButtonBox
                name="Show Spell Details"
                value={show_spell}
                onToggle={() => { setShowSpell(!show_spell); }}
              />
            </Grid>
            { show_spell && obj.spell &&
              <Grid
                size={{
                  xs: 12
                }}>
                <SpellDetails spell={obj} />
              </Grid>
            }
          </Grid>
        </div>
      );
    } else {
      return <></>
    }
  } else if (obj instanceof CharacterAbility) {
    const return_me: any[] = [];
    const the_ability = obj.the_ability;
    return_me.push(
      <Grid
        key="name"
        style={{ 
          lineHeight: "1.1",
          fontSize: "15px",
          fontWeight: "bold"
        }}
        size={{
          xs: 6
        }}>
        <ViewSpell spell={obj}
          character={character}
          fontSize={20}
          fontWeight={"bold"}
        />
      </Grid>
    );
    return_me.push(
      <Grid
        key="favorite"
        style={{ 
          lineHeight: "1.1",
          fontSize: "15px",
          fontWeight: "bold"
        }}
        size={{
          xs: 6
        }}>
        <StarBox
          name="Favorite"
          value={obj.favorite}
          onChange={(changed: boolean) => {
            obj.favorite = changed;
            character.changed();
            setPing(!ping);
          }}
        />
      </Grid>
    );
    if (the_ability) {
      if (the_ability.resource_consumed === "Slot" || type === "Spell") {
        // Make this work more like with a spell
        return_me.push(
          <Grid
            key="Cast"
            container
            spacing={0}
            direction="row"
            size={{
              xs: 12
            }}>
            <Grid
              size={{
                xs: 6
              }}>
              <CharacterCastButton
                obj={obj}
                action={action}
                character={character}
                char_interface={char_interface}
                level={selected_level}
                type={type}
                disabled={!enabled && !shared}
                shared={shared}
                // onChange={async () => {
                //   character.changed();
                //   await char_util.resourcesChanged(character);
                //   setPing(!ping);
                // }}
              />
            </Grid>
            <Grid
              size={{
                xs: 6
              }}>
              { renderLevelOptions() }
            </Grid>
          </Grid>
        );
      } else if (the_ability.resource_consumed === "Special") {
        // Make this work more like with a spell
        const old_used = char_interface.resource_holder.getSpecialResourceUsed(obj.true_id);
        return_me.push(
          <Grid
            key="Cast"
            container
            spacing={0}
            direction="row"
            size={{
              xs: 12
            }}>
            <Grid
              size={{
                xs: 6
              }}>
              <CharacterCastButton
                obj={obj}
                action={action}
                character={character}
                char_interface={char_interface}
                level={selected_level}
                type={type}
                disabled={!enabled && !shared}
                shared={shared}
                // onChange={async () => {
                //   character.changed();
                //   await char_util.resourcesChanged(character);
                //   setPing(!ping);
                // }}
              />
            </Grid>
            <Grid
              size={{
                xs: 6
              }}>
              <CharacterResourceBoxes 
                used={old_used}
                total={obj.special_resource_amount}
                true_total={obj.special_resource_amount}
                created={0}
                type={obj.name}
                show_type={false}
                character={character}
                shared={shared}
                onChange={(used: number) => {
                  const new_obj = obj;
                  if (new_obj instanceof CharacterAbility) {
                    char_interface.resource_holder.setSpecialResourceUsed(obj.true_id, used);
                  }
                  setPing(!ping);
                  character.subscription_holder.displayChanged(); // onObjChange(new_obj);
                }}
              />
            </Grid>
          </Grid>
        );
      } else if (the_ability.resource_consumed === "Charge") {
        const source_id = obj.char_feature?.use_item_id ?? "";
        const item_finder = character.items.find(o => o.true_id === source_id);
        if (item_finder) {
          const char_item = item_finder;
          // Make this work more like with a spell
          return_me.push(
            <Grid
              key="Cast"
              container
              spacing={0}
              direction="row"
              size={{
                xs: 12
              }}>
              <Grid
                size={{
                  xs: 6
                }}>
                <CharacterCastButton
                  obj={obj}
                  action={action}
                  character={character}
                  char_interface={char_interface}
                  level={selected_level}
                  disabled={!enabled && !shared}
                  shared={shared}
                  // onChange={async () => {
                  //   character.changed();
                  //   await char_util.resourcesChanged(character);
                  //   setPing(!ping);
                  // }}
                />
              </Grid>
              <Grid
                size={{
                  xs: 6
                }}>
                <CharacterResourceBoxes 
                  used={char_item.charges_used}
                  total={char_item.max_charges}
                  true_total={char_item.max_charges}
                  created={0}
                  type={"Charge"}
                  show_type={false}
                  character={character}
                  shared={shared}
                  onChange={async (used: number) => {
                    char_item.charges_used = used;
                    character.changed();
                    await char_util.resourcesChanged(character);
                    setPing(!ping);
                  }}
                />
              </Grid>
            </Grid>
          );
        }
      } else if (the_ability.resource_consumed === "None") {
        // Make this work more like with a spell
        return_me.push(
          <Grid
            key="Cast"
            container
            spacing={0}
            direction="row"
            size={{
              xs: 12
            }}>
            <Grid
              size={{
                xs: 12
              }}>
              <CharacterCastButton
                obj={obj}
                action={action}
                character={character}
                char_interface={char_interface}
                level={selected_level}
                disabled={!enabled && !shared}
                shared={shared}
                // onChange={() => {
                // }}
              />
            </Grid>
          </Grid>
        );
      } else {
        // It uses a resource from a ResourceFeature.
        // Find it.
        const crf_finder = character.resources.filter(o => o.type_id === the_ability.resource_consumed);
        if (crf_finder.length === 1) {
          const crf = crf_finder[0];
          return_me.push(
            <Grid
              key="Cast"
              container
              spacing={0}
              direction="row"
              size={{
                xs: 12
              }}>
              <Grid
                size={{
                  xs: 6
                }}>
                <CharacterCastButton
                  obj={obj}
                  action={action}
                  character={character}
                  char_interface={char_interface}
                  level={selected_level}
                  disabled={!enabled && !shared}
                  shared={shared}
                  // onChange={async () => {
                  //   character.changed();
                  //   await char_util.resourcesChanged(character);
                  //   setPing(!ping);
                  // }}
                />
              </Grid>
              <Grid
                size={{
                  xs: 6
                }}>
                <CharacterResourceBoxes 
                  used={crf.used}
                  total={crf.total}
                  true_total={crf.true_total}
                  created={crf.created}
                  type={crf.name}
                  show_type={true}
                  character={character}
                  shared={shared}
                  onChange={async (used: number) => {
                    crf.used = used;
                    character.changed();
                    await char_util.resourcesChanged(character);
                    setPing(!ping);
                  }}
                />
              </Grid>
            </Grid>
          );
        } else {
          console.log("Resource Not Found Error");
        }
      }
    }
    if (the_ability) {
      return_me.push(
        <Grid
          key="attacks"
          size={{
            xs: 12
          }}>
          <CharacterEffects 
            char={character}
            char_interface={char_interface}
            obj={obj}
            action={action}
            level={selected_level}
            wide
            casting_time={action.casting_time}
            disabled={!enabled}
            shared={shared}
            show_extra_effects
            onChange={(change_types: string[]) => {
              if (change_types.length > 0) {
                setPing(!ping);
                character.subscription_holder.recalculate();
              }
            }}
            // onTransform={() => {
            //   onTransform();
            // }}
          />
        </Grid>
      );
      const spell = obj;
      
      if (show_item || !spell.char_feature || spell.char_feature.use_item_id === "") {
        return_me.push(
          <Grid
            key="show_desc"
            size={{
              xs: 12
            }}>
            <ToggleButtonBox
              name={`${show_desc ? "Hide" : "Show"} Description`}
              value={show_desc}
              onToggle={() => { setShowDesc(!show_desc); }}
            />
          </Grid>
        );
      }
      if (show_desc) {
        if (spell.char_feature && spell.char_feature.use_item_id !== "") {
          const item_id = spell.char_feature.use_item_id;
          const item_finder = character.items.find(o => o.true_id === item_id);
          if (item_finder && show_item) {
            return_me.push(
              <Grid
                key="description"
                size={{
                  xs: 12
                }}>
                <ItemDetails character={character} item={item_finder} />
              </Grid>
            );
          }
        } else {
          return_me.push(
            <Grid
              key="description"
              size={{
                xs: 12
              }}>
              <DisplayStringWithReplacements display_string={ the_ability.description } cfs={char_interface} />
            </Grid>
          );
          for (const override of spell.spell_overrides.filter(o => o.char_feature)) {
            return_me.push(
              <Grid
                key={`${override.true_id}_name`}
                className="header"
                size={{
                  xs: 12
                }}>
                { override.name }
              </Grid>
            );
            return_me.push(
              <Grid
                key={`${override.true_id}_desc`}
                size={{
                  xs: 12
                }}>
                <DisplayStringWithReplacements display_string={ override.description } cfs={char_interface} />
              </Grid>
            );
          }
          const extra_effects = char_util.getExtraEffects({ char_int: char_interface, char: character, obj });
          
          for (const effect of extra_effects) {
            return_me.push(
              <Grid
                key={`${effect.true_id}_name`}
                className="header"
                size={{
                  xs: 12
                }}>
                { effect.name }
              </Grid>
            );
            return_me.push(
              <Grid
                key={`${effect.true_id}_desc`}
                size={{
                  xs: 12
                }}>
                <DisplayStringWithReplacements display_string={ effect.description } cfs={char_interface} />
              </Grid>
            );
            for (const cf of effect.char_features.filter(o => o.true_id !== effect.main_cf.true_id)) {
              return_me.push(
                <Grid
                  key={`${cf.true_id}_name`}
                  className="header"
                  size={{
                    xs: 12
                  }}>
                  { cf.name }
                </Grid>
              );
              return_me.push(
                <Grid
                  key={`${cf.true_id}_desc`}
                  size={{
                    xs: 12
                  }}>
                  <DisplayStringWithReplacements display_string={ cf.description } cfs={char_interface} />
                </Grid>
              );
            }
          }
        }
      }
    }
    
    if (obj.knowledge_usage_key !== "") {
      return_me.push(
        <Grid
          key="knowledge"
          style={{ fontWeight: "bold" }}
          size={{
            xs: 12
          }}>
          <Accordion>
            <AccordionSummary 
              style={{
                fontWeight: "bold",
                height: "30px"
              }}
              expandIcon={<ExpandMore />}>
              {obj.knowledge_usage_key} Knowledge
            </AccordionSummary>
            <AccordionDetails>
              <div>
                <CharacterKnowledgeForUsage 
                  character={character}
                  selected_usage={obj.knowledge_usage_key}
                />
              </div>
            </AccordionDetails>
          </Accordion>
        </Grid>
      );
    }
    if (obj.the_spell) {
      return_me.push(
        <Grid
          key="show_spell"
          size={{
            xs: 12
          }}>
          <ToggleButtonBox
            name={`${show_spell ? "Hide" : "Show"} Spell Details`}
            value={show_spell}
            onToggle={() => { setShowSpell(!show_spell); }}
          />
        </Grid>
      );
      if (show_spell) {
        return_me.push(
          <Grid
            key="spell_description"
            size={{
              xs: 12
            }}>
            <SpellDetails spell={obj.the_spell} />
          </Grid>
        );
      }
    }
    return (
      <div 
        style={{ 
          padding: "4px",
          fontSize: "11px"
        }}>
        <Grid container spacing={0} direction="row">
          { return_me }
          { renderCustomizations() }
        </Grid>
      </div>
    );
  } else {
    return <></>;
  }
}

